import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { lockInput, unlockInput } from './inputLock.js';
import { audioManager } from '../audio/AudioManager.js';
import { specRows, colorSwatch } from './carDetailsRows.mjs';
import { iconText } from './iconText.js';
import { enlargeForTouch } from './touchEnlarge.js';

// The showroom's spec sheet, drawn as a TABLE instead of a paragraph. Same furniture as
// Modal.js (dimmed overlay, white box with a dark stroke, ✕ top-right, a stack of wide
// yellow buttons) — the difference is the body: one row per fact, an emoji you can
// recognise before you can read the word next to it, and the number right-aligned in its
// own column so a seven-year-old can compare two cars by looking down one edge.
//
//   await showCarDetailsModal(scene, spec, [{ label: 'Kup za 8 000 zł', fn }]);
//
// Resolves with whatever the clicked action's `fn` returned, or null if the card was closed.

const W = 560;
const ROW_H = 32;
const BTN_H = 50, BTN_GAP = 14;
const TABLE_W = W - 72;
const TITLE_TOP = 20;      // title baseline offset from the top of the box
const SWATCH_Y = 68;       // the colour line, centred
const TABLE_TOP = 92;      // first row's top edge
const TABLE_GAP = 18;      // between the last row and the first button
const BOTTOM_PAD = 24;

const ROW_TINT = [0xffffff, 0xf1ece2];   // alternating, so the eye keeps its place
const CLOSE_LABEL = 'Zamknij';

function viewCentre(scene) {
  const cam = scene.cameras.main;
  return { ox: cam.scrollX, oy: cam.scrollY, x: cam.scrollX + GAME_WIDTH / 2, y: cam.scrollY + GAME_HEIGHT / 2 };
}

/**
 * `spec` is one entry of the server catalog's `cars` map. `actions` are the same
 * `{ label, fn?, disabled? }` objects showActionsModal takes; a „Zamknij” button is always
 * appended, and the ✕ / a click outside do the same thing.
 */
export function showCarDetailsModal(scene, spec, actions = []) {
  return new Promise((resolve) => {
    const rows = specRows(spec);
    const nBtn = actions.length + 1;                  // + Zamknij
    const tableH = rows.length * ROW_H;
    const H = TABLE_TOP + tableH + TABLE_GAP + nBtn * (BTN_H + BTN_GAP) + BOTTOM_PAD;
    const { ox, oy, x, y } = viewCentre(scene);
    const top = y - H / 2;
    const layer = scene.add.container(0, 0).setDepth(2000);
    lockInput(scene);

    const overlay = scene.add.rectangle(ox, oy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setOrigin(0, 0).setInteractive();
    const box = scene.add.rectangle(x, y, W, H, 0xffffff, 1).setStrokeStyle(4, 0x333333);
    const title = iconText(scene, x, top + TITLE_TOP, spec?.name_pl ?? 'Auto', {
      fontFamily: 'system-ui', fontSize: '26px', color: '#000', fontStyle: 'bold',
      align: 'center', wordWrap: { width: W - 90 },
    }).setOrigin(0.5, 0);
    layer.add([overlay, box, title]);
    enlargeForTouch(scene, layer, { x, y, w: W, h: H }, { overlay });

    // „kolor: czerwony” with the paint itself in a little chip beside it — the colour is the
    // one spec you can check by looking at the car, so it belongs next to its name, not in
    // the table of numbers.
    const colorPl = spec?.color_pl;
    if (colorPl) {
      const label = iconText(scene, 0, 0, `kolor: ${colorPl}`, {
        fontFamily: 'system-ui', fontSize: '17px', color: '#555',
      }).setOrigin(0, 0.5);
      const chipW = 22;
      const totalW = chipW + 8 + label.width;
      const chip = scene.add.rectangle(x - totalW / 2 + chipW / 2, top + SWATCH_Y, chipW, 16,
        colorSwatch(colorPl)).setStrokeStyle(2, 0x555555);
      label.setPosition(x - totalW / 2 + chipW + 8, top + SWATCH_Y);
      layer.add([chip, label]);
    }

    // The table. Three columns inside one tinted band per row: emoji, label, value —
    // the value right-aligned against the band's inner edge.
    const left = x - TABLE_W / 2;
    const right = x + TABLE_W / 2;
    rows.forEach((row, i) => {
      const cy = top + TABLE_TOP + i * ROW_H + ROW_H / 2;
      const band = scene.add.rectangle(x, cy, TABLE_W, ROW_H, ROW_TINT[i % 2], 1);
      const icon = iconText(scene, left + 12, cy, row.icon, {
        fontFamily: 'system-ui', fontSize: '19px',
      }).setOrigin(0, 0.5);
      const label = iconText(scene, left + 44, cy, row.label, {
        fontFamily: 'system-ui', fontSize: '18px', color: '#333',
      }).setOrigin(0, 0.5);
      const value = iconText(scene, right - 12, cy, row.value, {
        fontFamily: 'system-ui', fontSize: '18px', color: '#000', fontStyle: 'bold',
      }).setOrigin(1, 0.5);
      layer.add([band, icon, label, value]);
    });
    // One frame around the whole table, so it reads as a table and not as nine stripes.
    layer.add(scene.add.rectangle(x, top + TABLE_TOP + tableH / 2, TABLE_W, tableH)
      .setStrokeStyle(2, 0xbbb2a2));

    const close = (result) => { audioManager.playSfx('click'); unlockInput(scene); layer.destroy(); resolve(result); };

    const closeBtn = scene.add.rectangle(x + W / 2 - 24, top + 24, 36, 36, 0xaa3333, 1)
      .setStrokeStyle(2, 0x000000).setInteractive({ useHandCursor: true });
    const closeTxt = iconText(scene, x + W / 2 - 24, top + 24, '×', {
      fontFamily: 'system-ui', fontSize: '20px', color: '#fff', fontStyle: 'bold',
    }).setOrigin(0.5);
    layer.add([closeBtn, closeTxt]);
    closeBtn.on('pointerup', () => close(null));
    overlay.on('pointerup', () => close(null));

    const all = [...actions, { label: CLOSE_LABEL, quiet: true }];
    const firstY = top + TABLE_TOP + tableH + TABLE_GAP + BTN_H / 2;
    all.forEach((act, i) => {
      const by = firstY + i * (BTN_H + BTN_GAP);
      const fill = act.disabled ? 0xcccccc : act.quiet ? 0xe8e8e8 : 0xffd166;
      const btn = scene.add.rectangle(x, by, W - 90, BTN_H, fill).setStrokeStyle(2, 0x333333);
      const txt = iconText(scene, x, by, act.label, {
        fontFamily: 'system-ui', fontSize: '20px', color: act.disabled ? '#888' : '#000',
        fontStyle: 'bold', align: 'center', wordWrap: { width: W - 120 },
      }).setOrigin(0.5);
      layer.add([btn, txt]);
      if (act.disabled) return;
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerup', async () => {
        btn.disableInteractive();
        try {
          close(act.fn ? await act.fn() : null);
        } catch (e) {
          close({ error: e });
        }
      });
    });
  });
}
